// REMINDERS for one RF sensor — "if the door stays open, remind HER every hour
// until it is shut, and let her snooze it for 5 hours."
//
// ONE SCREEN, BOTH SHELLS (ui/shared): opened from the sensor's right-click menu
// and from the location's SecurityBar. Everything is settable — who, the state,
// the first delay, the interval, the snooze, the cap, a time window, days, and
// whether the premises must be 🔒 / 🔓.
//
// The SERVER does the reminding (Home Automation api/new/reminders.js, a
// one-minute sweep), so it works with every app shut. Rules and wording:
// core/reminders/reminderRules.js.
//
// ⚠️ ONLY A SENSOR WHOSE STATE CAN END (door / safe / shutter). On anything
// else the dialog says why instead of offering an editor — a reminder that
// nothing can stop would only end on its repeat cap.
//
// ⚠️ SOMEBODY ELSE'S REMINDER NEEDS THE SENSOR'S MANAGER (Super Admin, the
// location's owner, the sensor's own owner). Everyone else may set and snooze
// their OWN — and the server checks the same thing, because a dropdown is not a
// permission (CLAUDE.md).

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert, Box, Button, Checkbox, Chip, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, Divider, FormControlLabel, IconButton, MenuItem, Stack, Switch, TextField,
  Tooltip, Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import HistoryIcon from '@mui/icons-material/History';
import SnoozeIcon from '@mui/icons-material/Snooze';
import { useApp } from '../../core/state/AppContext';
import {
  getRfReminders, saveRfReminder, setRfReminderEnabled, deleteRfReminder,
  snoozeRfReminder, getRfReminderLog,
} from '../../core/api/api';
import {
  canRemind, cannotRemindText, remindableStates, ruleText, ruleStatus, ruleProblem,
  toSaveBody, minutesText, alsoRoomAlert, daysText, VOICE_REMIND, VOICE_CLOSED,
  AFTER_CHOICES, EVERY_CHOICES, SNOOZE_CHOICES, REPEAT_CHOICES, DEFAULT_RULE,
} from '../../core/reminders/reminderRules';
import { rfType } from '../../core/devices/rfTypes';
import { rfNumber } from '../../core/devices/rfIdentify';
import { soundOptions } from '../../core/push/pushChannels';
import { parseServerTime } from '../../core/devices/firmwareInfo';
import SoundPlayButton from './SoundPlayButton';
import { showToast } from './toast';
import { FIT_SM, FIT_MD } from './fitDialog';

const errText = (e, fallback) => e?.response?.data?.error || e?.message || fallback;
const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const STATUS_COLOR = {
  reminding: 'warning', snoozed: 'info', counting: 'default', waiting: 'default', stopped: 'default', off: 'default',
};

/** The status chip of one rule — recomputed every 30 s so a snooze counts down. */
export function StatusChip({ rule }) {
  const [, tick] = useState(0);
  useEffect(() => {
    const t = setInterval(() => tick((n) => n + 1), 30000);
    return () => clearInterval(t);
  }, []);
  const s = ruleStatus(rule, { serverTime: parseServerTime });
  if (!s.text) return null;
  return <Chip size="small" color={STATUS_COLOR[s.key] || 'default'} variant={s.key === 'reminding' ? 'filled' : 'outlined'} label={s.text} />;
}

// ── The editor ─────────────────────────────────────────────────────────────
function Editor({
  device, rule, candidates, manage, myId, onClose, onSaved,
}) {
  const [draft, setDraft] = useState(rule);
  const [busy, setBusy] = useState(false);
  const set = (k) => (v) => setDraft((d) => ({ ...d, [k]: v }));
  const states = remindableStates(device);
  const problem = ruleProblem(draft);
  const hasWindow = !!(draft.TimeFrom || draft.TimeTo);

  const save = async () => {
    setBusy(true);
    try {
      const r = await saveRfReminder(myId, toSaveBody(draft, { rfDeviceId: device.RfDeviceId, userId: myId }));
      // ⚠️ THIS BACKEND ANSWERS A REFUSAL AS HTTP 200 + { ok:false } — check the body.
      if (r.data && r.data.ok === false) { showToast(r.data.error || 'Not saved.', { severity: 'error' }); return; }
      showToast('Reminder saved', { severity: 'success' });
      onSaved();
    } catch (e) {
      showToast(errText(e, 'Not saved.'), { severity: 'error' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onClose={onClose} {...FIT_SM}>
      <DialogTitle sx={{ pb: 0.5 }}>{draft.ReminderId ? 'Edit reminder' : 'New reminder'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
            <TextField
              select size="small" label="Remind" sx={{ minWidth: 200 }}
              value={Number(draft.UserId) || ''}
              // ⚠️ Only the sensor's manager may name somebody else; for
              // everybody else this is themselves and nothing else.
              disabled={!manage}
              onChange={(e) => set('UserId')(Number(e.target.value))}
              helperText={manage ? 'Who gets the reminders' : 'Only the sensor’s owner can remind somebody else'}
            >
              {(candidates || []).map((u) => (
                <MenuItem key={u.UserId} value={Number(u.UserId)}>
                  {String(u.FullName || '').trim() || `User ${u.UserId}`}
                  {Number(u.UserId) === Number(myId) ? ' (you)' : ''}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select size="small" label="When it stays" sx={{ minWidth: 140 }}
              value={draft.Meaning || ''}
              onChange={(e) => set('Meaning')(e.target.value)}
            >
              {states.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
            </TextField>
          </Stack>

          <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
            <TextField
              select size="small" label="First reminder after" sx={{ minWidth: 150 }}
              value={Number(draft.AfterMin)} onChange={(e) => set('AfterMin')(Number(e.target.value))}
            >
              {AFTER_CHOICES.map((m) => <MenuItem key={m} value={m}>{minutesText(m)}</MenuItem>)}
            </TextField>
            <TextField
              select size="small" label="Then every" sx={{ minWidth: 120 }}
              value={Number(draft.EveryMin)} onChange={(e) => set('EveryMin')(Number(e.target.value))}
            >
              {EVERY_CHOICES.map((m) => <MenuItem key={m} value={m}>{minutesText(m)}</MenuItem>)}
            </TextField>
            <TextField
              select size="small" label="Snooze button" sx={{ minWidth: 120 }}
              value={Number(draft.SnoozeMin)} onChange={(e) => set('SnoozeMin')(Number(e.target.value))}
            >
              {SNOOZE_CHOICES.map((m) => <MenuItem key={m} value={m}>{minutesText(m)}</MenuItem>)}
            </TextField>
            <TextField
              select size="small" label="At most" sx={{ minWidth: 120 }}
              value={Number(draft.MaxRepeats)} onChange={(e) => set('MaxRepeats')(Number(e.target.value))}
              helperText="per opening"
            >
              {REPEAT_CHOICES.map((n) => <MenuItem key={n} value={n}>{`${n}×`}</MenuItem>)}
            </TextField>
          </Stack>

          <Divider />
          <Typography variant="subtitle2" className="fit-text">Only if</Typography>
          <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
            <FormControlLabel
              control={(
                <Checkbox
                  size="small" checked={hasWindow}
                  onChange={(e) => {
                    if (e.target.checked) setDraft((d) => ({ ...d, TimeFrom: '22:00', TimeTo: '07:00' }));
                    else setDraft((d) => ({ ...d, TimeFrom: '', TimeTo: '' }));
                  }}
                />
              )}
              label="Between"
            />
            <TextField
              type="time" size="small" sx={{ width: 120 }} disabled={!hasWindow}
              value={String(draft.TimeFrom || '').slice(0, 5)}
              onChange={(e) => set('TimeFrom')(e.target.value)}
            />
            <Typography variant="body2">and</Typography>
            <TextField
              type="time" size="small" sx={{ width: 120 }} disabled={!hasWindow}
              value={String(draft.TimeTo || '').slice(0, 5)}
              onChange={(e) => set('TimeTo')(e.target.value)}
            />
            <TextField
              select size="small" label="Premises" sx={{ minWidth: 150 }}
              value={draft.ModeIs || ''} onChange={(e) => set('ModeIs')(e.target.value)}
            >
              <MenuItem value="">Locked or not</MenuItem>
              <MenuItem value="locked">🔒 Locked only</MenuItem>
              <MenuItem value="unlocked">🔓 Unlocked only</MenuItem>
            </TextField>
          </Stack>
          <Box>
            <Typography variant="caption" color="text.secondary">
              {`Days — ${daysText(draft.Days) || 'every day'}`}
            </Typography>
            <Stack direction="row" spacing={0} flexWrap="wrap">
              {DAYS.map((d, i) => (
                <FormControlLabel
                  key={d}
                  sx={{ mr: 1 }}
                  control={(
                    <Checkbox
                      size="small"
                      checked={!!(Number(draft.Days) & (1 << i))}
                      onChange={(e) => set('Days')(e.target.checked
                        ? (Number(draft.Days) | (1 << i))
                        : (Number(draft.Days) & ~(1 << i)))}
                    />
                  )}
                  label={<Typography variant="caption">{d}</Typography>}
                />
              ))}
            </Stack>
          </Box>

          <Divider />
          {/* ⚠️ THE DEFAULTS SPEAK: "Please close room door", then "Room door
              closed". Both ship in the APK — a phone on an older build has no
              channel for them and falls back to the system sound, silently. */}
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            <TextField
              select size="small" label="Reminder sound" sx={{ minWidth: 230 }}
              value={draft.Sound || ''} onChange={(e) => set('Sound')(e.target.value)}
            >
              <MenuItem value="">Spoken — “Please close room door”</MenuItem>
              {soundOptions(draft.Sound).map((o) => <MenuItem key={o.sound} value={o.sound}>{o.name}</MenuItem>)}
            </TextField>
            <SoundPlayButton sound={draft.Sound || VOICE_REMIND} />
          </Stack>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            <FormControlLabel
              control={(
                <Checkbox
                  size="small" checked={Number(draft.ConfirmClose) === 1}
                  onChange={(e) => set('ConfirmClose')(e.target.checked ? 1 : 0)}
                />
              )}
              label="Say so when it is shut"
            />
            <TextField
              select size="small" label="Closed sound" sx={{ minWidth: 210 }}
              disabled={Number(draft.ConfirmClose) !== 1}
              value={draft.ClosedSound || ''} onChange={(e) => set('ClosedSound')(e.target.value)}
            >
              <MenuItem value="">Spoken — “Room door closed”</MenuItem>
              {soundOptions(draft.ClosedSound).map((o) => <MenuItem key={o.sound} value={o.sound}>{o.name}</MenuItem>)}
            </TextField>
            <SoundPlayButton sound={draft.ClosedSound || VOICE_CLOSED} />
          </Stack>
          {/* One message, only to somebody who was actually nagged, and never
              while a snooze is running or outside the rule's own window. */}
          {Number(draft.ConfirmClose) === 1 && (
            <Typography variant="caption" color="text.secondary" className="fit-text">
              Said once, and only if a reminder had already gone out — a door opened and shut
              inside the first delay stays quiet, and so does a snooze that is still running.
            </Typography>
          )}

          <Alert severity="info" icon={false}>{ruleText({ ...draft, UserName: (candidates || []).find((u) => Number(u.UserId) === Number(draft.UserId))?.FullName }, { withWho: true })}</Alert>
          {!!problem && <Alert severity="warning">{problem}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>Cancel</Button>
        <Button variant="contained" onClick={save} disabled={busy || !!problem}>Save</Button>
      </DialogActions>
    </Dialog>
  );
}

// ── History ────────────────────────────────────────────────────────────────
function LogDialog({ rule, myId, onClose }) {
  const [log, setLog] = useState(null);
  useEffect(() => {
    let alive = true;
    getRfReminderLog(rule.ReminderId, myId)
      .then((r) => { if (alive) setLog(Array.isArray(r.data?.log) ? r.data.log : []); })
      .catch(() => { if (alive) setLog([]); });
    return () => { alive = false; };
  }, [rule, myId]);
  return (
    <Dialog open onClose={onClose} {...FIT_SM}>
      <DialogTitle sx={{ pb: 0.5 }}>Reminder history</DialogTitle>
      <DialogContent>
        {log == null && <CircularProgress size={20} />}
        {log && !log.length && <Typography variant="body2" color="text.secondary" className="fit-text">Nothing yet.</Typography>}
        {log && log.map((l, i) => (
          <Box key={i} sx={{ py: 0.5, borderBottom: '1px solid', borderColor: 'divider' }}>
            <Typography variant="body2">
              <b>{l.Action}</b>
              {' — '}
              {l.Note}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {(() => { const t = parseServerTime(l.At); return t == null ? String(l.At || '') : new Date(t).toLocaleString(); })()}
            </Typography>
          </Box>
        ))}
      </DialogContent>
      <DialogActions><Button onClick={onClose}>Close</Button></DialogActions>
    </Dialog>
  );
}

// ── The list ───────────────────────────────────────────────────────────────
export default function RfRemindersDialog({
  open, onClose, device, room = null,
}) {
  const { userId } = useApp();
  const [state, setState] = useState({ loading: true });
  const [editing, setEditing] = useState(null);
  const [log, setLog] = useState(null);

  const load = useCallback(() => {
    if (!open || !device) return;
    setState({ loading: true });
    getRfReminders(userId, { rfDeviceId: device.RfDeviceId })
      .then((r) => setState({
        loading: false,
        // A 200 with ok:false = the migration has not run. Say so plainly
        // rather than show an editor whose Save can only fail.
        notSetUp: r.data?.ok === false,
        error: r.data?.ok === false ? (r.data.error || '') : '',
        rules: Array.isArray(r.data?.reminders) ? r.data.reminders : [],
        candidates: Array.isArray(r.data?.candidates) ? r.data.candidates : [],
        manage: !!r.data?.manage,
      }))
      .catch((e) => setState({ loading: false, error: errText(e, 'Could not load the reminders.'), rules: [] }));
  }, [open, device, userId]);
  useEffect(load, [load]);

  const mine = useMemo(
    () => (state.candidates || []).find((u) => Number(u.UserId) === Number(userId)),
    [state.candidates, userId],
  );
  const t = rfType(device?.RfType);
  const allowed = canRemind(device);
  const alsoRoom = alsoRoomAlert(room);

  const act = async (fn, okText) => {
    try {
      const r = await fn();
      if (r?.data && r.data.ok === false) { showToast(r.data.error || 'Not saved.', { severity: 'error' }); return; }
      if (okText) showToast(okText, { severity: 'success' });
      load();
    } catch (e) {
      showToast(errText(e, 'Not saved.'), { severity: 'error' });
    }
  };

  const canTouch = (rule) => state.manage || Number(rule.UserId) === Number(userId);

  if (!open || !device) return null;
  return (
    <>
      <Dialog open onClose={onClose} {...FIT_MD}>
        <DialogTitle sx={{ pb: 0.5 }}>
          {`Reminders — ${String(device.Name || '').trim() || t.label}`}
          <Typography variant="caption" color="text.secondary" display="block">
            {`${t.icon} ${t.label} · ${rfNumber(device.RfDeviceId)}`}
            {room?.RoomName || device.RoomName ? ` · ${room?.RoomName || device.RoomName}` : ''}
          </Typography>
        </DialogTitle>
        <DialogContent>
          {state.loading && <CircularProgress size={22} />}
          {!state.loading && !allowed && <Alert severity="info">{cannotRemindText(device)}</Alert>}
          {!state.loading && allowed && state.notSetUp && (
            <Alert severity="warning">
              {state.error || 'Reminders are not set up on this server yet.'}
              {' '}
              Run
              {' '}
              <code>reminders_migration.sql</code>
              .
            </Alert>
          )}
          {!state.loading && allowed && !state.notSetUp && (
            <>
              {!!state.error && <Alert severity="error" sx={{ mb: 1 }}>{state.error}</Alert>}
              <Typography variant="body2" color="text.secondary" className="fit-text" sx={{ mb: 1 }}>
                {`While this sensor stays in a state, the server reminds one named person again and again until it ends.
                  Each person has their own interval and their own snooze.`}
              </Typography>
              {!!alsoRoom && <Alert severity="info" sx={{ mb: 1 }}>{alsoRoom}</Alert>}
              {!state.rules.length && (
                <Typography variant="body2" color="text.secondary" className="fit-text">No reminders yet.</Typography>
              )}
              {state.rules.map((rule) => (
                <Box
                  key={rule.ReminderId}
                  sx={{
                    py: 1, borderBottom: '1px solid', borderColor: 'divider',
                    display: 'flex', alignItems: 'flex-start', gap: 1,
                  }}
                >
                  <Box sx={{ width: 0, flex: '1 1 0', minWidth: 220 }}>
                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                      <Typography variant="body2" fontWeight={700}>
                        {String(rule.UserName || '').trim() || `User ${rule.UserId}`}
                        {Number(rule.UserId) === Number(userId) ? ' (you)' : ''}
                      </Typography>
                      <StatusChip rule={rule} />
                    </Stack>
                    <Typography variant="caption" color="text.secondary">{ruleText(rule)}</Typography>
                  </Box>
                  <Stack direction="row" spacing={0.5} alignItems="center">
                    {ruleStatus(rule, { serverTime: parseServerTime }).key === 'snoozed' ? (
                      <Tooltip title="Cancel the snooze">
                        <IconButton
                          size="small" color="info" disabled={!canTouch(rule)}
                          onClick={() => act(() => snoozeRfReminder(rule.ReminderId, userId, 0), 'Snooze cancelled')}
                        >
                          <SnoozeIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    ) : (
                      <Tooltip title={`Snooze ${minutesText(rule.SnoozeMin)}`}>
                        <IconButton
                          size="small" disabled={!canTouch(rule)}
                          onClick={() => act(() => snoozeRfReminder(rule.ReminderId, userId), `Snoozed ${minutesText(rule.SnoozeMin)}`)}
                        >
                          <SnoozeIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    <Tooltip title="History">
                      <IconButton size="small" onClick={() => setLog(rule)}><HistoryIcon fontSize="small" /></IconButton>
                    </Tooltip>
                    <Tooltip title="Edit">
                      <IconButton size="small" disabled={!canTouch(rule)} onClick={() => setEditing(rule)}><EditIcon fontSize="small" /></IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton
                        size="small" color="error" disabled={!canTouch(rule)}
                        onClick={() => act(() => deleteRfReminder(rule.ReminderId, userId), 'Reminder deleted')}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Switch
                      size="small" checked={!!Number(rule.Enabled)} disabled={!canTouch(rule)}
                      onChange={(e) => act(
                        () => setRfReminderEnabled(rule.ReminderId, e.target.checked, userId),
                        e.target.checked ? 'Reminder on' : 'Reminder off',
                      )}
                    />
                  </Stack>
                </Box>
              ))}
            </>
          )}
        </DialogContent>
        <DialogActions>
          {allowed && !state.notSetUp && (
            <Button
              startIcon={<AddIcon />}
              onClick={() => setEditing({
                ...DEFAULT_RULE,
                Meaning: remindableStates(device)[0] || 'Open',
                UserId: state.manage ? 0 : Number(userId),
                RfDeviceId: device.RfDeviceId,
              })}
            >
              New reminder
            </Button>
          )}
          <Box sx={{ flex: 1 }} />
          <Button onClick={onClose}>Close</Button>
        </DialogActions>
      </Dialog>

      {editing && (
        <Editor
          device={device}
          rule={editing}
          candidates={state.manage ? state.candidates : (mine ? [mine] : [{ UserId: userId, FullName: 'You' }])}
          manage={!!state.manage}
          myId={userId}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
      {log && <LogDialog rule={log} myId={userId} onClose={() => setLog(null)} />}
    </>
  );
}

// ── Every reminder at a location ───────────────────────────────────────────
// The way in from the location's SecurityBar: one list of every rule on the
// estate's sensors, each with its snooze, and a tap to open that sensor's own
// reminders. Rules only — who may be reminded is a question per sensor, so
// editing drops into the sensor's dialog (the server checks it either way).
export function LocationRemindersDialog({
  open, onClose, locationId, locationName = '',
}) {
  const { userId } = useApp();
  const [state, setState] = useState({ loading: true });
  const [drill, setDrill] = useState(null);

  const load = useCallback(() => {
    if (!open || !locationId) return;
    setState({ loading: true });
    getRfReminders(userId, { locationId })
      .then((r) => setState({
        loading: false,
        notSetUp: r.data?.ok === false,
        error: r.data?.ok === false ? (r.data.error || '') : '',
        rules: Array.isArray(r.data?.reminders) ? r.data.reminders : [],
      }))
      .catch((e) => setState({ loading: false, error: errText(e, 'Could not load the reminders.'), rules: [] }));
  }, [open, locationId, userId]);
  useEffect(load, [load]);

  const snooze = async (rule) => {
    try {
      const r = await snoozeRfReminder(rule.ReminderId, userId);
      if (r.data && r.data.ok === false) showToast(r.data.error || 'Not snoozed.', { severity: 'error' });
      else showToast(`Snoozed ${minutesText(rule.SnoozeMin)}`, { severity: 'success' });
      load();
    } catch (e) {
      showToast(errText(e, 'Not snoozed.'), { severity: 'error' });
    }
  };

  if (!open) return null;
  return (
    <>
      <Dialog open onClose={onClose} {...FIT_MD}>
        <DialogTitle sx={{ pb: 0.5 }}>
          Reminders
          <Typography variant="caption" color="text.secondary" display="block">{locationName}</Typography>
        </DialogTitle>
        <DialogContent>
          {state.loading && <CircularProgress size={22} />}
          {!state.loading && state.notSetUp && (
            <Alert severity="warning">{state.error || 'Reminders are not set up on this server yet.'}</Alert>
          )}
          {!state.loading && !state.notSetUp && (
            <>
              {!!state.error && <Alert severity="error" sx={{ mb: 1 }}>{state.error}</Alert>}
              <Typography variant="body2" color="text.secondary" className="fit-text" sx={{ mb: 1 }}>
                A sensor left open reminds one named person again and again until it is shut.
                Add one from the sensor itself — its ⋮ / right-click menu, or its settings.
              </Typography>
              {!state.rules.length && (
                <Typography variant="body2" color="text.secondary" className="fit-text">No reminders here yet.</Typography>
              )}
              {state.rules.map((rule) => (
                <Box
                  key={rule.ReminderId}
                  sx={{
                    py: 1, borderBottom: '1px solid', borderColor: 'divider',
                    display: 'flex', alignItems: 'flex-start', gap: 1,
                  }}
                >
                  <Box sx={{ width: 0, flex: '1 1 0', minWidth: 220 }}>
                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                      <Typography variant="body2" fontWeight={700}>
                        {`${rfType(rule.RfType).icon} ${String(rule.Name || '').trim() || rfType(rule.RfType).label}`}
                        {rule.RoomName ? ` · ${rule.RoomName}` : ''}
                      </Typography>
                      <StatusChip rule={rule} />
                    </Stack>
                    <Typography variant="caption" color="text.secondary">{ruleText(rule)}</Typography>
                  </Box>
                  <Stack direction="row" spacing={0.5} alignItems="center">
                    <Tooltip title={`Snooze ${minutesText(rule.SnoozeMin)}`}>
                      <IconButton
                        size="small"
                        disabled={Number(rule.UserId) !== Number(userId)}
                        onClick={() => snooze(rule)}
                      >
                        <SnoozeIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Button
                      size="small"
                      onClick={() => setDrill({
                        RfDeviceId: rule.RfDeviceId,
                        Name: rule.Name,
                        RfType: rule.RfType,
                        RoomName: rule.RoomName,
                        LocationName: rule.LocationName,
                      })}
                    >
                      Open
                    </Button>
                  </Stack>
                </Box>
              ))}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Close</Button>
        </DialogActions>
      </Dialog>
      {drill && (
        <RfRemindersDialog
          open
          device={drill}
          onClose={() => { setDrill(null); load(); }}
        />
      )}
    </>
  );
}
